import * as Sentry from '@sentry/react';
import axios from 'axios';
import mixpanel from 'mixpanel-browser';
import moment from 'moment';
import 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { setBnlPreShoot } from '../../../actions/action_bnl_pre_shoot';
import { skipYearbookSelectionAndGoToOrder } from '../../../actions/yearbook';
import { getShootData } from '../../../actions/action_shoot';
import { applyLoadedShootData } from '../../../actions/shootContext';
import cartUtils from '../../../utils/cart';
import {
  ImageProtectionOverlay,
  useImageIntentFriction,
} from '../shared/protectedImage';
import {
  imageIntentGalleryFromDashboardGallery,
  NO_IMAGE_INTENT_GALLERY,
} from '../shared/protectedImage/imageIntentGallery';
import {
  buildGalleryOrderContext,
  buildGalleryOrderShootData,
} from './galleryOrderContext';
import {
  EditPoseLink,
  EventWrapper,
  GalleryImage,
  GalleryImageContainer,
  GalleryImageContainerWrapper,
  GalleryImageName,
  GalleryOrderButton,
  GalleryWrapper,
  GroupGalleryImage,
  SelectPoseBanner,
  SelectedBanner,
  StudentCard,
  StudentName,
  ViewGalleryButton,
} from './dashboardGalleriesStyles';

const DashboardGalleries = ({ history }) => {
  // TODO: move to a const file?
  const BASE_IMAGE_URL = `https://${process.env.IMAGE_PREVIEW_BUCKET}.s3.amazonaws.com/`;
  const BASE_WATERMARKED_IMAGE_URL = `https://${process.env.IMAGE_PREVIEW_WATERMARKED_BUCKET}.s3.amazonaws.com/`;

  const dispatch = useDispatch();

  const orders = useSelector((state) => state.v3Order.orders);
  const skipYearbookSelection = useSelector(
    (state) => state.yearbookPoses?.skipSelectionForSession,
  );
  const currentUserId = useSelector((state) => state.auth.currentUserId);
  // The dashboard lists every child's galleries: each tile names its own
  // gallery, and Cmd/Ctrl+S (a page save) is credited to none of them.
  const { getFrictionProps } = useImageIntentFriction({
    surface: 'dashboard',
    ...NO_IMAGE_INTENT_GALLERY,
  });

  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dataIsLoading, setDataIsLoading] = useState(false);

  useEffect(() => {
    if (currentUserId) {
      mixpanel.identify(currentUserId);

      // Optionally, you might also want to set profile properties here
      mixpanel.people.set({
        // Set any relevant user properties
      });
    }
  }, [currentUserId]);

  useEffect(() => {
    let isMounted = true; // Flag to track mounted status

    const fetchData = async () => {
      try {
        setIsLoading(true); // Start loading
        const response = await axios.get('/api/v2/dashboard');

        if (isMounted) {
          setDashboardData(response.data); // Only update state if component is mounted
          setIsLoading(false); // Only update state if component is mounted
        }
      } catch (error) {
        // console.error('Failed to fetch galleries: ', error);
        if (isMounted) {
          setIsLoading(false); // Only update state if component is mounted
        }
      }
    };

    fetchData();

    // Cleanup function to set isMounted to false when component unmounts
    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array to run the effect only once
  const renderBanner = (student) => {
    const gallery = findActionableYearbookGallery(student);

    if (!gallery) return null;

    return (
      <SelectPoseBanner>
        {`Important: please select a yearbook pose by ${moment(
          gallery.yearbook_selection_deadline,
        ).format('MMMM DD, YYYY')}`}
        <ViewGalleryButton
          onClick={() => {
            dispatch(skipYearbookSelectionAndGoToOrder(false));
            goToOrder(student, gallery, false, true);
          }}
        >
          View Gallery
        </ViewGalleryButton>
      </SelectPoseBanner>
    );
  };

  const handleImageClick = (student, gallery) => () => {
    if (
      gallery.requires_yearbook_selection &&
      !gallery.yearbook_selection_performed &&
      isBeforeDeadline(
        gallery.yearbook_selection_deadline,
        gallery.studio_time_zone,
      )
    ) {
      goToOrder(student, gallery);
    }
  };

  const handleEditPoseClick = (student, gallery) => () => {
    goToOrder(student, gallery, true);
  };
  // TODO: can we remove this? i.e. is it needed in v3?
  // const setUnlockedGroups = (studentId) => {
  //   const orderState = this.props.cart.orders[studentId];
  //   let bnlUnlockedGroups = [];

  //   if (orderState) {
  //     bnlUnlockedGroups = this.cloneArray(this.props.cart.bnlUnlockedGroups);

  //     orderState.cartItems.forEach((cartItem) => {
  //       if (cartItem.lock_type === 'unlocker') {
  //         cartItem.lock_group.forEach((set) => {
  //           if (!bnlUnlockedGroups.includes(set)) bnlUnlockedGroups.push(set);
  //         });
  //       }
  //     });
  //   }

  //   dispatch(updateBnlUnlockedGroups(bnlUnlockedGroups));
  // };

  const isBeforeDeadline = (deadline, studioTimeZone) => {
    const nowInStudioTZ = moment().tz(studioTimeZone);
    const deadlineInStudioTZ = moment
      .tz(deadline, 'YYYY-MM-DD', studioTimeZone)
      .endOf('day');

    return nowInStudioTZ.isBefore(deadlineInStudioTZ);
  };

  const shouldShowBanner = (student) => {
    return Boolean(findActionableYearbookGallery(student));
  };

  const findActionableYearbookGallery = (student) => {
    return student.galleries.find(
      (gallery) =>
        gallery.requires_yearbook_selection &&
        !gallery.yearbook_selection_performed &&
        isBeforeDeadline(
          gallery.yearbook_selection_deadline,
          gallery.studio_time_zone,
        ),
    );
  };

  const showGalleryLoadError = () => {
    Swal.fire({
      title: 'Unable to load gallery',
      text: 'We could not load this gallery. Please try again. If the problem continues, reload the dashboard.',
      icon: 'error',
      confirmButtonText: 'Okay',
    });
  };

  const goToOrder = (
    student,
    gallery,
    isEdit = false,
    forceYearbookSelection = false,
  ) => {
    const requiresYearbookSelection = gallery.requires_yearbook_selection;
    const studioTimeZone = gallery.studio_time_zone;
    const yearbookSelectionDeadline = gallery.yearbook_selection_deadline;

    const hasYearbookSelection = gallery.yearbook_selection_performed;

    const v3CartContainsPrepayOrders =
      cartUtils.cartContainsPrepayOrders(orders);

    if (v3CartContainsPrepayOrders) {
      Swal.fire({
        title: 'Your cart contains prepay orders',
        html: '<p>Your cart contains prepay orders, which cannot be submitted with gallery-based orders.</p><p>Please submit or clear your prepay orders to enable gallery-based ordering.</p>',
        icon: 'warning',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Okay',
      });

      Sentry.setContext('orders', {
        orders: orders,
        orderKeys: Object.keys(orders),
      });

      Sentry.captureMessage('pre-order in progress warning');

      return;
    }

    const galleryOrderContext = buildGalleryOrderContext({
      dashboardData,
      gallery,
      student,
    });
    const data = buildGalleryOrderShootData({ student, gallery });

    setDataIsLoading(true);

    dispatch(setBnlPreShoot(false));

    dispatch(getShootData(data))
      .then((resp) => {
        const shootData = resp?.payload?.data;

        if (!shootData || shootData.errors) {
          showGalleryLoadError();
          return;
        }

        // TODO: can we remove this? i.e. is it needed in v3?
        // setUnlockedGroups(student.id);

        // Unpacking the shoot payload into the store is shared with the
        // cart's bonus re-add, which loads a non-active order's shoot the
        // same way (ENG-2043) — see actions/shootContext.
        dispatch(applyLoadedShootData(shootData, galleryOrderContext));

        // TODO: do we need to set this for v3?
        // that.props.setCpmId(cpmId);

        if (
          isEdit ||
          (requiresYearbookSelection &&
            !hasYearbookSelection &&
            (forceYearbookSelection || !skipYearbookSelection))
        ) {
          const nowInStudioTZ = moment().tz(studioTimeZone);
          const deadline = moment
            .tz(yearbookSelectionDeadline, 'YYYY-MM-DD', studioTimeZone)
            .endOf('day');

          if (!nowInStudioTZ.isAfter(deadline)) {
            return history.push(`/v3/yearbook-selection/${student.id}`);
          }
        }

        history.push('/v3/incentive-offers');
      })
      .catch(() => {
        showGalleryLoadError();
      })
      .finally(() => {
        setDataIsLoading(false);
      });
  };

  return (
    <>
      <div className={dataIsLoading ? 'loading' : 'hidden'}>
        <div className="spinner" />
      </div>
      {!dashboardData && isLoading && (
        <div>
          <p>loading...</p>
        </div>
      )}
      {dashboardData && !isLoading && (
        <div>
          {dashboardData.students.map((student) => {
            return (
              <StudentCard key={student.id}>
                {shouldShowBanner(student) && renderBanner(student)}
                <StudentName translate="no">
                  {student.first_name} {student.last_name}
                </StudentName>
                {student.galleries.map((gallery) => {
                  if (
                    !Array.isArray(gallery.images) ||
                    gallery.images.length === 0
                  ) {
                    return null;
                  }

                  return (
                    <EventWrapper key={gallery.id}>
                      <div key={gallery.event_name}>
                        <strong>{gallery.event_name}</strong>
                        {gallery.shoot_date && (
                          <div style={{ color: '#888' }}>
                            Date:{' '}
                            {moment(gallery.shoot_date).format('MMMM D, YYYY')}
                          </div>
                        )}
                        <div>{gallery.gallery_job_created_at_year}</div>
                        <GalleryOrderButton
                          className="order-from-gallery-button"
                          onClick={() => {
                            goToOrder(student, gallery);
                          }}
                        >
                          order from this gallery
                        </GalleryOrderButton>
                        {gallery.yearbook_selection_performed &&
                          isBeforeDeadline(
                            gallery.yearbook_selection_deadline,
                            gallery.studio_time_zone,
                          ) && (
                            <EditPoseLink
                              onClick={handleEditPoseClick(student, gallery)}
                            >
                              Edit yearbook pose selection
                            </EditPoseLink>
                          )}
                        <GalleryWrapper>
                          {gallery.images.map((image) => {
                            // If image has cropped_images array with content, render those
                            if (
                              image.cropped_images &&
                              image.cropped_images.length > 0
                            ) {
                              return image.cropped_images.map((cropImage) => {
                                // Determine image source for crop image
                                let cropImageSrc = '';
                                if (
                                  cropImage.watermarked_preview_filename ===
                                    undefined &&
                                  cropImage.url !== null &&
                                  cropImage.url !== undefined &&
                                  cropImage.url !== ''
                                ) {
                                  cropImageSrc = cropImage.url;
                                } else {
                                  cropImageSrc =
                                    cropImage.watermarked_preview_filename !==
                                      null &&
                                    cropImage.watermarked_preview_filename !==
                                      undefined
                                      ? `${BASE_WATERMARKED_IMAGE_URL}${cropImage.watermarked_preview_filename}`
                                      : `${BASE_IMAGE_URL}${cropImage.web_preview_filename}`;
                                }

                                // The save shield reports with the tile's own
                                // context, so wrapping never changes the
                                // gallery or the throttle bucket.
                                const cropContext = {
                                  ...imageIntentGalleryFromDashboardGallery(
                                    gallery,
                                  ),
                                  surface: 'dashboard',
                                  metadata: {
                                    image_id: cropImage.id,
                                    kind: 'crop',
                                  },
                                };

                                return (
                                  <GalleryImageContainerWrapper
                                    key={
                                      cropImage.web_preview_filename ||
                                      cropImage.id
                                    }
                                  >
                                    <GalleryImageContainer>
                                      <GalleryImage
                                        {...getFrictionProps(cropContext)}
                                        onClick={handleImageClick(
                                          student,
                                          gallery,
                                        )}
                                        src={cropImageSrc}
                                        alt="Gallery Image Preview"
                                      />
                                      {cropImage.yearbook_selection && (
                                        <SelectedBanner>
                                          ✅ Selected Pose
                                        </SelectedBanner>
                                      )}
                                      <ImageProtectionOverlay
                                        context={cropContext}
                                        imageSrc={cropImageSrc}
                                      />
                                    </GalleryImageContainer>
                                    <GalleryImageName>
                                      {cropImage.name || image.name}
                                    </GalleryImageName>
                                  </GalleryImageContainerWrapper>
                                );
                              });
                            }

                            // Otherwise, render the original image (existing code)
                            // TODO: revisit this logic
                            let imageSrc = '';

                            if (
                              image.watermarked_preview_filename ===
                                undefined &&
                              image.url !== null &&
                              image.url !== undefined &&
                              image.url !== ''
                            ) {
                              imageSrc = image.url;
                            } else {
                              imageSrc =
                                image.watermarked_preview_filename !== null &&
                                image.watermarked_preview_filename !== undefined
                                  ? `${BASE_WATERMARKED_IMAGE_URL}${image.watermarked_preview_filename}`
                                  : `${BASE_IMAGE_URL}${image.web_preview_filename}`;
                            }

                            const imageContext = {
                              ...imageIntentGalleryFromDashboardGallery(
                                gallery,
                              ),
                              surface: 'dashboard',
                              metadata: {
                                image_id: image.id,
                                kind: 'original',
                              },
                            };

                            return (
                              <GalleryImageContainerWrapper
                                key={image.web_preview_filename || image.id}
                              >
                                <GalleryImageContainer>
                                  <GalleryImage
                                    {...getFrictionProps(imageContext)}
                                    onClick={handleImageClick(student, gallery)}
                                    src={imageSrc}
                                    alt="Gallery Image Preview"
                                  />
                                  {image.yearbook_selection && (
                                    <SelectedBanner>
                                      ✅ Selected Pose
                                    </SelectedBanner>
                                  )}
                                  <ImageProtectionOverlay
                                    context={imageContext}
                                    imageSrc={imageSrc}
                                  />
                                </GalleryImageContainer>
                                <GalleryImageName>
                                  {image.name}
                                </GalleryImageName>
                              </GalleryImageContainerWrapper>
                            );
                          })}
                          {gallery.associated_group_images.map((image) => {
                            let imageSrc = '';

                            if (
                              image.watermarked_preview_filename ===
                                undefined &&
                              image.url !== null &&
                              image.url !== undefined &&
                              image.url !== ''
                            ) {
                              imageSrc = image.url;
                            } else {
                              imageSrc =
                                image.watermarked_preview_filename !== null &&
                                image.watermarked_preview_filename !== undefined
                                  ? `${BASE_WATERMARKED_IMAGE_URL}${image.watermarked_preview_filename}`
                                  : `${BASE_IMAGE_URL}${image.web_preview_filename}`;
                            }

                            const groupContext = {
                              // Group photos belong to the separate group
                              // gallery, whose key the page does not have.
                              ...NO_IMAGE_INTENT_GALLERY,
                              surface: 'dashboard',
                              metadata: {
                                image_id: image.id,
                                kind: 'group',
                              },
                            };

                            return (
                              <GalleryImageContainer
                                key={image.web_preview_filename}
                              >
                                <GroupGalleryImage
                                  {...getFrictionProps(groupContext)}
                                  src={imageSrc}
                                  alt="Gallery Image Preview"
                                />
                                {image.yearbook_selection && (
                                  <SelectedBanner>
                                    ✅ Selected Pose
                                  </SelectedBanner>
                                )}
                                <ImageProtectionOverlay
                                  context={groupContext}
                                  imageSrc={imageSrc}
                                />
                              </GalleryImageContainer>
                            );
                          })}
                        </GalleryWrapper>
                      </div>
                    </EventWrapper>
                  );
                })}
              </StudentCard>
            );
          })}
        </div>
      )}
    </>
  );
};

export default DashboardGalleries;
